<template>
  <div>
    <button ref="container"
            id="typeformNextButton"
            @keydown.enter="nextStep"
            @click="nextStep"
            :class="animateClass"
            style="--anim-delay: 200ms;"
            type="button"
            class="flex space-x-4 items-center rounded-md focus:outline-none focus:brightness-90 group">
      <span
        class="block pointer-events-none relative px-5 py-2.5 rounded-md font-bold text-xl hover:shadow-lg hover:brightness-90 transition-all group-focus:ring-2 group-focus:ring-offset-2"
        :class="isEnterPressed && 'brightness-70'"
        :style="[invertedColors, twRingColors, loadingColor]">
          <span v-if="loading" aria-label="Loading"
                class="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center z-[1]">
          <span class="dot"/>
          <span class="dot"/>
          <span class="dot"/>
        </span>
        <span :class="loading && 'opacity-0'" class="pointer-events-none">
          <slot>{{ typeformIsLastStep ? "Submit Application" : defaultText }}</slot>
        </span>
      </span>
      <span class="text-xs items-center gap-2 cursor-text hidden lg:flex">
        <span>Press <b>Enter</b></span>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3"><polyline
          points="9 10 4 15 9 20"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg>
      </span>
    </button>
  </div>
</template>

<script setup>
import {computed, inject, onBeforeUnmount, onMounted, ref} from "vue";
import {useAnimateInTrigger} from "../composables/AnimateInComposable";

const emits = defineEmits(["nextStep"]);
const typeformData = inject("typeformData", {});
const typeformIsLastStep = inject("typeformIsLastStep");

const props = defineProps({
  isLastStep: Boolean,
  isFirstStep: Boolean,
  animate: Boolean,
  loading: Boolean,
  defaultText: {
    type: String,
    default: "Ok"
  },
});

const invertedColors = computed(() => `background: ${typeformData.primaryForeground};color: ${typeformData.primaryBackground};`);
const twRingColors = computed(() => `--tw-ring-offset-color:${typeformData.primaryBackground};--tw-ring-color:${typeformData.primaryForeground};`);
const loadingColor = computed(() => `--typeform-loading-color:${typeformData.primaryBackground};`);

const container = ref();
const isEnterPressed = ref(false);
const animateClass = ref(props.animate ? "animate-in-vars-pre" : "");

const nextStep = () => emits("nextStep");

if (props.animate) useAnimateInTrigger(container, (className) => animateClass.value = className);

let enterPressedTimeout;

const handleTypeformEnter = () => {
  isEnterPressed.value = true;
  clearTimeout(enterPressedTimeout);

  enterPressedTimeout = setTimeout(() => isEnterPressed.value = false, 200);
};

onMounted(() => window.addEventListener("typeform:enter", handleTypeformEnter));
onBeforeUnmount(() => window.removeEventListener("typeform:enter", handleTypeformEnter));
</script>

<style scoped>
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin: 0 5px;
  animation: bounce 0.6s infinite;
}

@keyframes bounce {
  0%, 50% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-6px);
  }
}

.dot:nth-of-type(1) {
  background-color: var(--typeform-loading-color);
  animation-delay: 0s;
}

.dot:nth-of-type(2) {
  background-color: var(--typeform-loading-color);
  animation-delay: 0.08s;
}

.dot:nth-of-type(3) {
  background-color: var(--typeform-loading-color);
  animation-delay: 0.16s;
}
</style>
